import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRightIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { fetchCategoryTree } from '@/store/slices/categoriesSlice';
import { Category } from '@/types';
import { cn } from '@/utils/cn';
import { getImageUrl } from '@/utils/imageUtils';

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

export function MegaMenu({ isOpen, onClose, className }: MegaMenuProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { categoryTree, isTreeLoading } = useSelector((state: RootState) => state.categories);
  const [hoveredCategory, setHoveredCategory] = useState<Category | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch category tree on mount
  useEffect(() => {
    if (isOpen && categoryTree.length === 0) {
      dispatch(fetchCategoryTree());
    }
  }, [isOpen, categoryTree.length, dispatch]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Handle category hover with debounce
  const handleCategoryHover = (category: Category) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredCategory(category);
    }, 300);
  };

  // Handle category leave
  const handleCategoryLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  // Clear hover state when menu closes
  useEffect(() => {
    if (!isOpen) {
      setHoveredCategory(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className={cn(
          'absolute top-full left-0 right-0 mt-2',
          'bg-white dark:bg-dark-850 border border-gray-200 dark:border-dark-700',
          'rounded-lg shadow-2xl z-50',
          className
        )}
        style={{ minHeight: '400px', maxHeight: 'calc(100vh - 120px)' }}
      >
        <div className="flex" style={{ height: '100%', maxHeight: 'calc(100vh - 120px)' }}>
          {/* Left Panel - Categories List */}
          <div className="w-64 border-r border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Squares2X2Icon className="h-5 w-5" />
                All Categories
              </h3>

              {isTreeLoading ? (
                <div className="space-y-2">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="h-10 bg-gray-200 dark:bg-dark-700 rounded animate-pulse"
                    />
                  ))}
                </div>
              ) : (
                <nav className="space-y-1">
                  {categoryTree.map((category) => (
                    <div
                      key={category.id}
                      onMouseEnter={() => handleCategoryHover(category)}
                      onMouseLeave={handleCategoryLeave}
                    >
                      <Link
                        to={`/category/${category.slug}`}
                        onClick={onClose}
                        className={cn(
                          'flex items-center justify-between px-3 py-2 rounded-lg',
                          'text-sm font-medium transition-colors',
                          hoveredCategory?.id === category.id
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {category.image_url ? (
                            <img
                              src={getImageUrl(category.image_url)}
                              alt={category.name}
                              className="w-6 h-6 rounded object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded bg-gray-200 dark:bg-dark-600 flex items-center justify-center">
                              <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                                {category.name[0]}
                              </span>
                            </div>
                          )}
                          <span className="truncate">{category.name}</span>
                        </div>

                        {category.children && category.children.length > 0 && (
                          <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
                        )}
                      </Link>
                    </div>
                  ))}
                </nav>
              )}
            </div>
          </div>

          {/* Right Panel - Subcategories */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            {hoveredCategory ? (
              <div className="p-6">
                {/* Subcategories */}
                {hoveredCategory.children && hoveredCategory.children.length > 0 ? (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      {hoveredCategory.name}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {hoveredCategory.children.map((subcategory) => (
                        <Link
                          key={subcategory.id}
                          to={`/category/${subcategory.slug}`}
                          onClick={onClose}
                          className={cn(
                            'flex flex-col items-center gap-3 p-4 rounded-lg',
                            'border border-gray-200 dark:border-dark-600',
                            'hover:border-primary-300 dark:hover:border-primary-700',
                            'hover:bg-primary-50 dark:hover:bg-primary-900/10',
                            'hover:shadow-md transition-all duration-200 group'
                          )}
                        >
                          {subcategory.image_url ? (
                            <img
                              src={getImageUrl(subcategory.image_url)}
                              alt={subcategory.name}
                              className="w-16 h-16 rounded object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex items-center justify-center">
                              <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                                {subcategory.name[0]}
                              </span>
                            </div>
                          )}
                          <p className="text-sm font-medium text-gray-900 dark:text-white text-center group-hover:text-primary-600 dark:group-hover:text-primary-400">
                            {subcategory.name}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-12">
                    <Link
                      to={`/category/${hoveredCategory.slug}`}
                      onClick={onClose}
                      className="text-center"
                    >
                      {hoveredCategory.image_url && (
                        <img
                          src={hoveredCategory.image_url}
                          alt={hoveredCategory.name}
                          className="w-32 h-32 rounded-lg object-cover mx-auto mb-4"
                          loading="lazy"
                        />
                      )}
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {hoveredCategory.name}
                      </h4>
                      <p className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                        View all products →
                      </p>
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500 dark:text-gray-400">
                  <Squares2X2Icon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Hover over a category to see subcategories</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Mobile version of MegaMenu (accordion style)
export function MegaMenuMobile({ isOpen, onClose }: MegaMenuProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { categoryTree, isTreeLoading } = useSelector((state: RootState) => state.categories);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && categoryTree.length === 0) {
      dispatch(fetchCategoryTree());
    }
  }, [isOpen, categoryTree.length, dispatch]);

  if (!isOpen) return null;

  const toggleCategory = (categoryId: number) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -300 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 lg:hidden"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Menu Panel */}
      <div className="absolute left-0 top-0 bottom-0 w-80 bg-white dark:bg-dark-850 shadow-xl overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Squares2X2Icon className="h-6 w-6" />
              All Categories
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          {isTreeLoading ? (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 dark:bg-dark-700 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {categoryTree.map((category) => (
                <div key={category.id} className="border-b border-gray-200 dark:border-dark-700 pb-2">
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/category/${category.slug}`}
                      onClick={onClose}
                      className="flex-1 flex items-center gap-3 px-3 py-3 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-dark-800 rounded-lg"
                    >
                      {category.image_url ? (
                        <img
                          src={getImageUrl(category.image_url)}
                          alt={category.name}
                          className="w-8 h-8 rounded object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-gray-200 dark:bg-dark-600 flex items-center justify-center">
                          <span className="text-sm font-bold text-gray-500 dark:text-gray-400">
                            {category.name[0]}
                          </span>
                        </div>
                      )}
                      <span className="font-medium">{category.name}</span>
                    </Link>

                    {category.children && category.children.length > 0 && (
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className="p-2 text-gray-500 dark:text-gray-400"
                      >
                        <ChevronRightIcon
                          className={cn(
                            'h-5 w-5 transition-transform',
                            expandedCategory === category.id && 'rotate-90'
                          )}
                        />
                      </button>
                    )}
                  </div>

                  {/* Subcategories */}
                  <AnimatePresence>
                    {expandedCategory === category.id &&
                      category.children &&
                      category.children.length > 0 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="ml-6 mt-2 space-y-1 overflow-hidden"
                        >
                          {category.children.map((subcategory) => (
                            <Link
                              key={subcategory.id}
                              to={`/category/${subcategory.slug}`}
                              onClick={onClose}
                              className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-800 rounded-lg"
                            >
                              {subcategory.image_url ? (
                                <img
                                  src={getImageUrl(subcategory.image_url)}
                                  alt={subcategory.name}
                                  className="w-6 h-6 rounded object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-6 h-6 rounded bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                                  <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                                    {subcategory.name[0]}
                                  </span>
                                </div>
                              )}
                              <span>{subcategory.name}</span>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
